import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Heading, Button, Badge, Icon, useToast, useColorModeValue,
  Table, Thead, Tbody, Tr, Th, Td, Spinner, IconButton, useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  FormControl, FormLabel, FormHelperText, Input, Select, Switch, Textarea, NumberInput, NumberInputField,
  Divider, Alert, AlertIcon, AlertDescription, Tag, TagLabel, Code,
} from '@chakra-ui/react';
import {
  FiPlus, FiEdit2, FiTrash2, FiPlay, FiRefreshCw, FiZap, FiArrowRight, FiArrowLeft,
} from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';

const TRIGGER_TYPES = [
  { value: 'deal_stage_changed',  label: 'Deal stage changed',     direction: 'outbound' },
  { value: 'deal_field_changed',  label: 'Deal field changed',     direction: 'outbound' },
  { value: 'deal_tag_added',      label: 'Deal tag added',         direction: 'outbound' },
  { value: 'arive_loan_created',  label: 'Arive: loan created',    direction: 'inbound' },
  { value: 'arive_lead_created',  label: 'Arive: lead created',    direction: 'inbound' },
  { value: 'scheduled_pull',      label: 'Scheduled pipeline pull', direction: 'inbound' },
];

const ACTION_TYPES = {
  outbound: [
    { value: 'create_in_arive', label: 'Create lead/loan in Arive' },
    { value: 'update_in_arive', label: 'Update existing Arive entity' },
  ],
  inbound: [
    { value: 'create_in_origin', label: 'Create deal in Origin' },
  ],
};

const FIELD_OPERATORS = [
  { value: 'eq',       label: 'equals' },
  { value: 'neq',      label: 'not equals' },
  { value: 'gte',      label: '>=' },
  { value: 'lte',      label: '<=' },
  { value: 'gt',       label: '>' },
  { value: 'lt',       label: '<' },
  { value: 'exists',   label: 'is set' },
  { value: 'missing',  label: 'is empty' },
  { value: 'contains', label: 'contains text' },
];

// Default empty rule for "Add Rule"
const blankRule = (scope = 'company', ownerId = null) => ({
  id: null,
  scope,
  owner_id: ownerId,
  direction: 'outbound',
  trigger_type: 'deal_stage_changed',
  trigger_config: { toStage: 'loan-application-intake', requireMissingAriveId: true },
  action_type: 'create_in_arive',
  action_config: { entityType: 'loan' },
  is_enabled: true,
  priority: 100,
  metadata: { description: '' },
});

// -----------------------------------------------------------------------------
// Dynamic form bits per trigger / action type
// -----------------------------------------------------------------------------

function TriggerConfigForm({ rule, onChange }) {
  const tt = rule.trigger_type;
  const cfg = rule.trigger_config || {};
  const update = (patch) => onChange({ ...rule, trigger_config: { ...cfg, ...patch } });

  if (tt === 'deal_stage_changed') {
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs">Trigger on deal stage equal to</FormLabel>
          <Input size="sm" value={cfg.toStage || ''} onChange={(e) => update({ toStage: e.target.value })}
            placeholder="loan-application-intake" />
          <FormHelperText fontSize="xs">Slug form (e.g. lead-quoted, loan-application-intake). Leave blank to fire on ANY stage change.</FormHelperText>
        </FormControl>
        <FormControl display="flex" alignItems="center">
          <FormLabel fontSize="xs" mb={0} flex={1}>Only fire when deal is NOT yet linked to Arive</FormLabel>
          <Switch size="sm" isChecked={cfg.requireMissingAriveId === true}
            onChange={(e) => update({ requireMissingAriveId: e.target.checked })} />
        </FormControl>
        <FormControl display="flex" alignItems="center">
          <FormLabel fontSize="xs" mb={0} flex={1}>Only fire when deal IS already linked to Arive</FormLabel>
          <Switch size="sm" isChecked={cfg.requireExistingAriveId === true}
            onChange={(e) => update({ requireExistingAriveId: e.target.checked })} />
        </FormControl>
      </VStack>
    );
  }

  if (tt === 'deal_field_changed') {
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs">Deal field name</FormLabel>
          <Input size="sm" value={cfg.field || ''} onChange={(e) => update({ field: e.target.value })}
            placeholder="amount" />
          <FormHelperText fontSize="xs">Column name on crm_deals (e.g., amount, source_medium, is_at_risk)</FormHelperText>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Operator</FormLabel>
          <Select size="sm" value={cfg.operator || 'eq'} onChange={(e) => update({ operator: e.target.value })}>
            {FIELD_OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FormControl>
        {!['exists', 'missing'].includes(cfg.operator) && (
          <FormControl>
            <FormLabel fontSize="xs">Value</FormLabel>
            <Input size="sm" value={cfg.value ?? ''} onChange={(e) => update({ value: e.target.value })} />
          </FormControl>
        )}
      </VStack>
    );
  }

  if (tt === 'deal_tag_added') {
    return (
      <FormControl>
        <FormLabel fontSize="xs">Tag name</FormLabel>
        <Input size="sm" value={cfg.tagName || ''} onChange={(e) => update({ tagName: e.target.value })}
          placeholder="ready-for-arive" />
        <FormHelperText fontSize="xs">Fires when this tag is added to a deal (case-sensitive).</FormHelperText>
      </FormControl>
    );
  }

  if (tt === 'arive_loan_created' || tt === 'arive_lead_created') {
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs">Match user by</FormLabel>
          <Select size="sm" value={cfg.matchBy || ''} onChange={(e) => update({ matchBy: e.target.value || undefined })}>
            <option value="">No match required (any incoming entity)</option>
            <option value="originator_email">Arive originator email = Origin user email</option>
          </Select>
          <FormHelperText fontSize="xs">Restricts which Arive entities trigger auto-create in Origin.</FormHelperText>
        </FormControl>
      </VStack>
    );
  }

  if (tt === 'scheduled_pull') {
    return (
      <Alert status="info" borderRadius="md" fontSize="xs">
        <AlertIcon />
        <AlertDescription>
          Schedule for scheduled pulls is configured per-user in CRM Settings &raquo; Arive LOS. This rule controls only WHAT happens with newly discovered loans/leads.
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}

function ActionConfigForm({ rule, onChange }) {
  const at = rule.action_type;
  const cfg = rule.action_config || {};
  const update = (patch) => onChange({ ...rule, action_config: { ...cfg, ...patch } });

  if (at === 'create_in_arive') {
    return (
      <FormControl>
        <FormLabel fontSize="xs">Entity to create in Arive</FormLabel>
        <Select size="sm" value={cfg.entityType || 'auto'} onChange={(e) => update({ entityType: e.target.value })}>
          <option value="auto">Auto (pick lead or loan based on deal stage)</option>
          <option value="lead">Always lead</option>
          <option value="loan">Always loan</option>
        </Select>
      </FormControl>
    );
  }

  if (at === 'update_in_arive') {
    return (
      <Alert status="info" borderRadius="md" fontSize="xs">
        <AlertIcon />
        <AlertDescription>
          No additional configuration. Will sync the deal's current stage to its linked Arive entity.
        </AlertDescription>
      </Alert>
    );
  }

  if (at === 'create_in_origin') {
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs">Default Origin stage for new deals</FormLabel>
          <Input size="sm" value={cfg.defaultOriginStage || ''} onChange={(e) => update({ defaultOriginStage: e.target.value })}
            placeholder="lead-new" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Default Origin owner (Firebase uid)</FormLabel>
          <Input size="sm" value={cfg.defaultOwnerId || ''} onChange={(e) => update({ defaultOwnerId: e.target.value })}
            placeholder="Leave blank to use {{owner.id}} from rule context" />
          <FormHelperText fontSize="xs">Templates: {`{{owner.id}}`}, {`{{arive.originatorEmail}}`}</FormHelperText>
        </FormControl>
      </VStack>
    );
  }

  return null;
}

// -----------------------------------------------------------------------------
// Rule editor modal
// -----------------------------------------------------------------------------
export function RuleEditorModal({ isOpen, onClose, rule, onSave, saving, lockScope = null }) {
  const [draft, setDraft] = useState(rule);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const toastDryRun = useToast();

  useEffect(() => { setDraft(rule); setTestResult(null); }, [rule]);

  if (!draft) return null;

  const handleDryRun = async () => {
    const input = window.prompt(
      draft.direction === 'outbound'
        ? 'Enter the Origin deal UUID to dry-run this rule against:'
        : 'Enter the Arive originator email to dry-run this rule against:'
    );
    if (!input) return;
    setTesting(true);
    setTestResult(null);
    try {
      const fn = httpsCallable(functionsUsEast1, 'testAriveSyncRule');
      const args = draft.direction === 'outbound'
        ? {
            ruleId: draft.id,
            direction: 'outbound',
            dealId: input.trim(),
            triggerOverride: {
              triggerType: draft.trigger_type,
              toStage: draft.trigger_config?.toStage,
              fromStage: draft.trigger_config?.fromStage || null,
            },
          }
        : {
            ruleId: draft.id,
            direction: 'inbound',
            ariveEntity: {
              entityType: draft.trigger_type === 'arive_lead_created' ? 'lead' : 'loan',
              originatorEmail: input.trim(),
              ariveId: 'dry-run',
            },
          };
      const res = await fn(args);
      setTestResult(res.data || null);
    } catch (err) {
      toastDryRun({ title: 'Dry-run failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setTesting(false);
    }
  };

  // When direction changes, reset trigger_type + action_type to a sensible default
  const setDirection = (direction) => {
    const firstTrigger = TRIGGER_TYPES.find((t) => t.direction === direction).value;
    const firstAction = ACTION_TYPES[direction][0].value;
    setDraft({
      ...draft,
      direction,
      trigger_type: firstTrigger,
      trigger_config: {},
      action_type: firstAction,
      action_config: {},
    });
  };

  const setTriggerType = (triggerType) => {
    setDraft({ ...draft, trigger_type: triggerType, trigger_config: {} });
  };

  const setActionType = (actionType) => {
    setDraft({ ...draft, action_type: actionType, action_config: {} });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{draft.id ? 'Edit Sync Rule' : 'New Sync Rule'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea size="sm" rows={2}
                value={draft.metadata?.description || ''}
                onChange={(e) => setDraft({ ...draft, metadata: { ...(draft.metadata || {}), description: e.target.value } })}
                placeholder="e.g. Auto-create the loan in Arive when an Origin deal reaches Application Intake"
              />
            </FormControl>

            <HStack spacing={4}>
              {!lockScope && (
                <FormControl>
                  <FormLabel fontSize="sm">Scope</FormLabel>
                  <Select size="sm" value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value, owner_id: e.target.value === 'company' ? null : draft.owner_id })}>
                    <option value="company">Company (applies to all users)</option>
                    <option value="user">User (single Firebase uid)</option>
                  </Select>
                </FormControl>
              )}
              <FormControl>
                <FormLabel fontSize="sm">Direction</FormLabel>
                <Select size="sm" value={draft.direction} onChange={(e) => setDirection(e.target.value)}>
                  <option value="outbound">Outbound (Origin &rarr; Arive)</option>
                  <option value="inbound">Inbound (Arive &rarr; Origin)</option>
                </Select>
              </FormControl>
            </HStack>

            {draft.scope === 'user' && !lockScope && (
              <FormControl>
                <FormLabel fontSize="sm">Owner (Firebase uid)</FormLabel>
                <Input size="sm" value={draft.owner_id || ''} onChange={(e) => setDraft({ ...draft, owner_id: e.target.value })} placeholder="abc123..." />
              </FormControl>
            )}

            <Divider />

            <Box>
              <Heading size="sm" mb={2}>Trigger</Heading>
              <VStack align="stretch" spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Trigger type</FormLabel>
                  <Select size="sm" value={draft.trigger_type} onChange={(e) => setTriggerType(e.target.value)}>
                    {TRIGGER_TYPES.filter((t) => t.direction === draft.direction).map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Select>
                </FormControl>
                <TriggerConfigForm rule={draft} onChange={setDraft} />
              </VStack>
            </Box>

            <Divider />

            <Box>
              <Heading size="sm" mb={2}>Action</Heading>
              <VStack align="stretch" spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Action type</FormLabel>
                  <Select size="sm" value={draft.action_type} onChange={(e) => setActionType(e.target.value)}>
                    {ACTION_TYPES[draft.direction].map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </Select>
                </FormControl>
                <ActionConfigForm rule={draft} onChange={setDraft} />
              </VStack>
            </Box>

            <Divider />

            <HStack spacing={4}>
              <FormControl flex={1}>
                <FormLabel fontSize="sm">Priority</FormLabel>
                <NumberInput size="sm" value={draft.priority} min={0} max={1000}
                  onChange={(_str, num) => setDraft({ ...draft, priority: Number.isFinite(num) ? num : 100 })}>
                  <NumberInputField />
                </NumberInput>
                <FormHelperText fontSize="xs">Lower = higher priority.</FormHelperText>
              </FormControl>
              <FormControl display="flex" alignItems="center" flex={1}>
                <FormLabel fontSize="sm" mb={0} flex={1}>Enabled</FormLabel>
                <Switch size="md" isChecked={draft.is_enabled !== false} onChange={(e) => setDraft({ ...draft, is_enabled: e.target.checked })} />
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>
        {testResult && (
          <Box px={6} pb={4}>
            <Alert
              status={testResult.wouldFire ? 'success' : 'info'}
              borderRadius="md"
              fontSize="sm"
              flexDirection="column"
              alignItems="flex-start"
            >
              <HStack>
                <AlertIcon />
                <Text fontWeight="semibold">
                  {testResult.wouldFire ? 'Rule would fire' : 'Rule would NOT fire'}
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600" mt={1}>{testResult.reason}</Text>
              {testResult.actualWinner && !testResult.wouldFire && (
                <Text fontSize="xs" color="orange.600" mt={1}>
                  Higher-priority winner: <Code fontSize="10px">{testResult.actualWinner.id}</Code>
                </Text>
              )}
            </Alert>
          </Box>
        )}
        <ModalFooter>
          {draft.id && (
            <Button mr="auto" variant="outline" colorScheme="purple" leftIcon={<Icon as={FiPlay} />}
              onClick={handleDryRun} isLoading={testing}>
              Dry-run
            </Button>
          )}
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={() => onSave(draft)} isLoading={saving}>
            {draft.id ? 'Save' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------
export default function AriveSyncRulesAdmin() {
  const toast = useToast();
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBG = useColorModeValue('gray.50', 'gray.700');

  const [rules, setRules]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [draining, setDraining] = useState(false);
  const [activeRule, setActiveRule] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'getAriveSyncRules');
      const res = await fn({});
      setRules(res.data?.rules || []);
    } catch (err) {
      toast({ title: 'Failed to load rules', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (draft) => {
    setSaving(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'upsertAriveSyncRule');
      await fn(draft);
      toast({ title: draft.id ? 'Rule updated' : 'Rule created', status: 'success', duration: 2500 });
      onClose();
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.metadata?.description || rule.id}"?`)) return;
    try {
      const fn = httpsCallable(functionsUsEast1, 'deleteAriveSyncRule');
      await fn({ id: rule.id });
      toast({ title: 'Rule deleted', status: 'info', duration: 2500 });
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const handleDrainQueue = async () => {
    setDraining(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'drainAriveSyncQueueNow');
      const res = await fn({});
      const s = res.data || {};
      toast({
        title: 'Queue drained',
        description: `drained=${s.drained || 0} completed=${s.completed || 0} skipped=${s.skipped || 0} failed=${s.failed || 0}`,
        status: 'success',
        duration: 5000,
      });
    } catch (err) {
      toast({ title: 'Drain failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setDraining(false);
    }
  };

  const openNew = (scope) => {
    setActiveRule(blankRule(scope));
    onOpen();
  };
  const openEdit = (rule) => {
    setActiveRule(rule);
    onOpen();
  };

  const companyRules = rules.filter((r) => r.scope === 'company');
  const userRules    = rules.filter((r) => r.scope === 'user');

  return (
    <Box bg={bg} borderRadius="xl" p={6} borderWidth="1px" borderColor={borderColor}>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="lg">Arive Sync Rules</Heading>
            <Text fontSize="sm" color="gray.600">
              Customizable auto-sync between Origin deals and Arive entities. Company defaults apply to every LO; per-user rules override them.
            </Text>
          </VStack>
          <HStack>
            <Button size="sm" leftIcon={<Icon as={FiRefreshCw} />} variant="outline" onClick={load} isLoading={loading}>
              Refresh
            </Button>
            <Button size="sm" leftIcon={<Icon as={FiPlay} />} colorScheme="purple" variant="outline"
              onClick={handleDrainQueue} isLoading={draining}>
              Drain queue now
            </Button>
          </HStack>
        </HStack>

        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            Rules fire from the <Code fontSize="xs">crm_arive_sync_queue</Code> worker every 1 minute. Use{' '}
            <strong>Drain queue now</strong> for immediate feedback during testing.
          </AlertDescription>
        </Alert>

        {/* Company rules */}
        <Box>
          <HStack justify="space-between" mb={3}>
            <Heading size="md">Company defaults</Heading>
            <Button size="sm" leftIcon={<Icon as={FiPlus} />} colorScheme="blue" onClick={() => openNew('company')}>
              Add company rule
            </Button>
          </HStack>
          {loading ? <Spinner /> : <RuleTable rules={companyRules} onEdit={openEdit} onDelete={handleDelete} headerBG={headerBG} />}
        </Box>

        <Divider />

        {/* User rules (read-only audit) */}
        <Box>
          <HStack justify="space-between" mb={3}>
            <Heading size="md">Per-user rules</Heading>
            <Tag size="sm"><TagLabel>Audit view</TagLabel></Tag>
          </HStack>
          {loading ? <Spinner /> : userRules.length === 0 ? (
            <Text fontSize="sm" color="gray.500">No per-user rules configured yet.</Text>
          ) : <RuleTable rules={userRules} onEdit={null} onDelete={null} headerBG={headerBG} />}
        </Box>
      </VStack>

      <RuleEditorModal
        isOpen={isOpen}
        onClose={onClose}
        rule={activeRule}
        onSave={handleSave}
        saving={saving}
      />
    </Box>
  );
}

export { blankRule };
export function RuleTable({ rules, onEdit, onDelete, headerBG }) {
  if (rules.length === 0) {
    return <Text fontSize="sm" color="gray.500">No rules.</Text>;
  }
  return (
    <Box overflowX="auto" borderWidth="1px" borderRadius="md">
      <Table size="sm" variant="simple">
        <Thead bg={headerBG}>
          <Tr>
            <Th>Description</Th>
            <Th>Direction</Th>
            <Th>Trigger</Th>
            <Th>Action</Th>
            <Th>Priority</Th>
            <Th>Status</Th>
            {(onEdit || onDelete) && <Th></Th>}
          </Tr>
        </Thead>
        <Tbody>
          {rules.map((rule) => (
            <Tr key={rule.id}>
              <Td maxW="280px">
                <Text fontSize="xs" noOfLines={2}>{rule.metadata?.description || <em>(no description)</em>}</Text>
                {rule.scope === 'user' && rule.owner_id && (
                  <Code fontSize="10px" mt={1}>{rule.owner_id}</Code>
                )}
              </Td>
              <Td>
                <Badge colorScheme={rule.direction === 'outbound' ? 'blue' : 'purple'} fontSize="10px">
                  <Icon as={rule.direction === 'outbound' ? FiArrowRight : FiArrowLeft} boxSize={3} mr={1} />
                  {rule.direction}
                </Badge>
              </Td>
              <Td>
                <Code fontSize="10px">{rule.trigger_type}</Code>
                {rule.trigger_config?.toStage && <Text fontSize="10px" color="gray.500">stage = {rule.trigger_config.toStage}</Text>}
                {rule.trigger_config?.tagName && <Text fontSize="10px" color="gray.500">tag = {rule.trigger_config.tagName}</Text>}
              </Td>
              <Td>
                <Code fontSize="10px">{rule.action_type}</Code>
                {rule.action_config?.entityType && <Text fontSize="10px" color="gray.500">{rule.action_config.entityType}</Text>}
              </Td>
              <Td>{rule.priority}</Td>
              <Td>
                {rule.is_enabled ? (
                  <Badge colorScheme="green" fontSize="10px">Enabled</Badge>
                ) : (
                  <Badge colorScheme="gray" fontSize="10px">Disabled</Badge>
                )}
              </Td>
              {(onEdit || onDelete) && (
                <Td>
                  <HStack spacing={1}>
                    {onEdit && (
                      <IconButton size="xs" icon={<FiEdit2 />} aria-label="Edit" variant="ghost"
                        onClick={() => onEdit(rule)} />
                    )}
                    {onDelete && (
                      <IconButton size="xs" icon={<FiTrash2 />} aria-label="Delete" variant="ghost" colorScheme="red"
                        onClick={() => onDelete(rule)} />
                    )}
                  </HStack>
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}
